import { CheckCircle2, Circle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DocumentIcon } from '~/@mid_ui/icons/DocumentIcon'
import { FileCopyIcon } from '~/@mid_ui/icons/FileCopyIcon'
import { ProfileIcon } from '~/@mid_ui/icons/ProfileIcon'
import { SmileyIcon } from '~/@mid_ui/icons/SmileyIcon'
import { WalletIcon } from '~/@mid_ui/icons/WalletIcon'
import { Text } from '~/@mid_ui/typography/Text'
import { StepHeadline } from '~/@pages_onboarding/_onboarding-stepper/StepHeadline'
import { cn } from '~/shadcn/lib/utils'
import { Spinner } from '~/shadcn/ui/spinner'

/**
 * Kroky zobrazované počas `finishRegistration` (BE generuje dokumenty + schvaľuje účet,
 * FE k tomu pridáva `sleep(4000)`). Poradie zhruba kopíruje `MidOnboardingService.finishRegistration`.
 * Posledný krok zostáva zobrazený, kým request nedobehne.
 * Ikony majú vlastný viewBox, veľkosť riadi `className` (CSS prebije width/height atribúty).
 */
const signingProgressSteps = [
  { label: 'Generujeme rámcovú zmluvu', Icon: DocumentIcon },
  { label: 'Ukladáme podpísané dokumenty', Icon: FileCopyIcon },
  { label: 'Zakladáme váš investorský profil', Icon: ProfileIcon },
  { label: 'Pripravujeme váš účet', Icon: WalletIcon },
  { label: 'Dokončujeme, už len chvíľku', Icon: SmileyIcon },
] as const

const signingProgressStepDurationMs = 1200

function useSigningProgressStep() {
  const [activeStepIndex, setActiveStepIndex] = useState(0)

  useEffect(() => {
    const lastStepIndex = signingProgressSteps.length - 1
    const intervalId = setInterval(() => {
      setActiveStepIndex((currentIndex) =>
        currentIndex >= lastStepIndex ? lastStepIndex : currentIndex + 1
      )
    }, signingProgressStepDurationMs)

    return () => clearInterval(intervalId)
  }, [])

  return activeStepIndex
}

function SigningProgressScreen() {
  const activeStepIndex = useSigningProgressStep()
  const activeStep = signingProgressSteps[activeStepIndex]
  const ActiveStepIcon = activeStep.Icon
  const progressPercentage = Math.round(
    ((activeStepIndex + 1) / signingProgressSteps.length) * 100
  )

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-5">
        <StepHeadline headingText="Podpisujeme vašu rámcovú zmluvu" />
        <Text className="font-medium">
          Všetko prebieha tak, ako má. Trvá to len pár sekúnd, prosím
          nezatvárajte stránku.
        </Text>
      </div>

      <div className="flex flex-col items-center gap-8 w-full py-6">
        {/* Ilustrácia: ikona aktuálneho kroku v jemnom modrom kruhu, spinner ako badge v rohu */}
        <div className="relative flex items-center justify-center size-24 rounded-full bg-accent/10 text-accent">
          <ActiveStepIcon
            key={activeStepIndex}
            className="size-10 animate-[signing-step-fade-in_400ms_ease-out]"
            aria-hidden="true"
          />
          <span className="absolute -bottom-1 -right-1 flex items-center justify-center size-9 rounded-full bg-white shadow-sm">
            <Spinner className="size-5 text-accent" aria-hidden="true" />
          </span>
        </div>

        <div className="flex flex-col items-center gap-3">
          <Text
            key={activeStepIndex}
            aria-live="polite"
            className="text-center font-semibold text-accent animate-[signing-step-fade-in_400ms_ease-out]">
            {activeStep.label}…
          </Text>
        </div>

        <div
          className="w-full max-w-sm h-2 rounded-full bg-accent/20 overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPercentage}
          aria-label="Priebeh podpisu">
          <div
            className="h-full rounded-full bg-accent transition-[width] duration-700 ease-out"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      <ul className="flex flex-col gap-3 w-full" aria-label="Kroky podpisu">
        {signingProgressSteps.map(({ label: stepLabel }, stepIndex) => {
          const isStepDone = stepIndex < activeStepIndex
          const isStepActive = stepIndex === activeStepIndex

          return (
            <li
              key={stepLabel}
              className={cn(
                'flex items-center gap-3 font-semibold transition-colors duration-300',
                isStepDone || isStepActive ? 'text-accent' : 'text-[#bbb]'
              )}>
              {isStepDone && (
                <CheckCircle2
                  className="w-[22px] h-[22px] shrink-0"
                  aria-hidden="true"
                />
              )}
              {isStepActive && (
                <Spinner
                  className="w-[22px] h-[22px] shrink-0"
                  aria-hidden="true"
                />
              )}
              {!isStepDone && !isStepActive && (
                <Circle
                  className="w-[22px] h-[22px] shrink-0"
                  aria-hidden="true"
                />
              )}
              {stepLabel}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export { SigningProgressScreen }
